import type {
  DictsysInflectionSchemeCell,
  DictsysInflectionSchemeContent,
  DictsysInflectionSchemeHeaderElement,
  DictsysInflectionSchemeSpanishCommonCompoundTense,
  DictsysInflectionSchemeSpanishPerfectTense,
  DictsysInflectionSchemeSpanishVerbBasicTense,
  DictsysInflectionSchemeSpanishVerbImperativePerson,
  DictsysInflectionSchemeSpanishVerbImperativeTense,
  DictsysInflectionSchemeSpanishVerbMood,
  DictsysInflectionSchemeSpanishVerbPerson,
  DictsysInflectionSchemeSpanishVerbTense,
  DictsysInflectionSchemeTable,
  DictsysSpanishVerbInflectionScheme
} from "@/types";

import { DictsysInflectionSchemeHelper } from ".";

type Mood = keyof DictsysInflectionSchemeSpanishVerbMood;
type Person = keyof DictsysInflectionSchemeSpanishVerbPerson;
type ImperativePerson = keyof DictsysInflectionSchemeSpanishVerbImperativePerson;
type BasicTense = keyof DictsysInflectionSchemeSpanishVerbBasicTense;
type Tense = keyof DictsysInflectionSchemeSpanishVerbTense;
type ImperativeTense = keyof DictsysInflectionSchemeSpanishVerbImperativeTense;
type PerfectTense = keyof DictsysInflectionSchemeSpanishPerfectTense;
type CommonCompoundTense = keyof DictsysInflectionSchemeSpanishCommonCompoundTense;

const pronounList: string[] = ["yo", "tú", "él, ella, Ud.", "nosotros, -as", "vosotros, -as", "ellos, ellas, Uds."];
const shortPronounList = pronounList.map((pronoun) => pronoun.replace(/^.+, (Uds?\.)/, "$1"));

const speaker: Person[] = [
  "first_person_singular",
  "second_person_singular",
  "third_person_he_singular",
  "first_person_plural",
  "second_person_plural",
  "third_person_plural"
];

const imperativeSpeaker: ("first_person_singular" | ImperativePerson)[] = [
  "first_person_singular",
  "second_person_singular",
  "third_person_singular",
  "first_person_plural",
  "second_person_plural",
  "third_person_plural"
];

const basicTenses: BasicTense[] = ["present", "imperfect", "future"];
const tenses: Tense[] = ["present", "imperfect", "simple_past", "future", "conditional"];
const perfectTenses: PerfectTense[] = ["present_perfect", "past_perfect", "future_perfect"];
const commonCompoundTenses: CommonCompoundTense[] = [...perfectTenses, "conditional_perfect"];
const imperativeTenses: ImperativeTense[] = ["affirmative", "negative"];

export function headWord(inflectionScheme: DictsysSpanishVerbInflectionScheme): DictsysInflectionSchemeHeaderElement[] {
  const elements: DictsysInflectionSchemeHeaderElement[] = [];

  const { verb } = inflectionScheme;

  if (verb === undefined) {
    return elements;
  }

  if (verb.infinitive) {
    elements.push({
      type: "headWord",
      texts: verb.infinitive.present.map((content) => content.inflexion),
      tense: "infinitiv"
    });
  }

  if (verb.gerundium) {
    elements.push({
      type: "inflection",
      texts: verb.gerundium.present.map((content) => content.inflexion),
      tense: "gerundium"
    });
  }

  if (verb.participle) {
    elements.push({
      type: "inflection",
      texts: verb.participle.present.map((content) => content.inflexion),
      tense: "participium"
    });
  }

  return elements;
}

export function getTables(inflectionScheme: DictsysSpanishVerbInflectionScheme): DictsysInflectionSchemeTable[] {
  if (!inflectionScheme.verb) {
    return [];
  }

  const { verb } = inflectionScheme;

  return [
    getTable(verb, "indicative"),
    getTable(verb, "common_compound_tenses"),
    getTable(verb, "subjunctive"),
    getTable(verb, "perfect_tenses"),
    getTable(verb, "imperative")
  ];
}

function getTable(verb: DictsysInflectionSchemeSpanishVerbMood, mood: Mood): DictsysInflectionSchemeTable {
  const table: DictsysInflectionSchemeTable = { header: "", cells: [], separatorIndex: 3 };

  switch (mood) {
    case "indicative": {
      const time = verb[mood];

      table.header = "Indikativ";
      table.cells = [
        [
          { type: "sub-header", elements: [{}] },
          { elements: [{ texts: ["Præsens"], comment: "(nutid)" }], type: "sub-header" },
          { elements: [{ texts: ["Imperfektum"], comment: "(uafsluttet datid)" }], type: "sub-header" },
          { elements: [{ texts: ["Præteritum"], comment: "(afsluttet datid)" }], type: "sub-header" },
          {
            elements: [{ texts: ["Futurum"], comment: "(fremtid)" }],
            type: "sub-header",
            extraTable: {
              ...getTable(verb, "near_future"),
              text: "Se nær fremtid med ir + a + infinitiv",
              label: "Gå til nær fremtid med ir + a + infinitiv"
            }
          },
          { elements: [{ texts: ["Konditionalis"], comment: "(fortids fremtid)" }], type: "sub-header" }
        ]
      ];

      pronounList.forEach((pronoun, index) => {
        const cells: DictsysInflectionSchemeCell[] = [];
        cells.push({ type: "sub-header", elements: [{ texts: [pronoun] }] });

        tenses.forEach((tense) => {
          const person = speaker[index];
          cells.push(getBodyCell(time[tense][person]));
        });

        table.cells.push(cells);
      });

      break;
    }

    case "common_compound_tenses": {
      const time = verb[mood];

      table.header = "Indikativ, sammensatte tider";
      table.cells = [
        [
          { type: "sub-header", elements: [{}] },
          { elements: [{ texts: ["Perfektum"], comment: "(førnutid)" }], type: "sub-header" },
          { elements: [{ texts: ["Pluskvamperfektum"], comment: "(førdatid)" }], type: "sub-header" },
          { elements: [{ texts: ["Futurum perfektum"], comment: "(førfremtid)" }], type: "sub-header" },
          { elements: [{ texts: ["Konditionalis perfektum"], comment: "(fortids førfremtid)" }], type: "sub-header" }
        ]
      ];

      pronounList.forEach((pronoun, index) => {
        const cells: DictsysInflectionSchemeCell[] = [];
        cells.push({ type: "sub-header", elements: [{ texts: [pronoun] }] });

        commonCompoundTenses.forEach((tense) => {
          const person = speaker[index];
          cells.push(getBodyCell(time[tense][person]));
        });

        table.cells.push(cells);
      });

      break;
    }

    case "subjunctive": {
      const time = verb[mood];

      table.header = "Konjunktiv";
      table.cells = [
        [
          { type: "sub-header", elements: [{}] },
          { elements: [{ texts: ["Præsens"], comment: "(nutid)" }], type: "sub-header" },
          { elements: [{ texts: ["Imperfektum"], comment: "(uafsluttet datid)" }], type: "sub-header" },
          { elements: [{ texts: ["Futurum"], comment: "(fremtid)" }], type: "sub-header" }
        ]
      ];

      pronounList.forEach((pronoun, index) => {
        const cells: DictsysInflectionSchemeCell[] = [];
        cells.push({ type: "sub-header", elements: [{ texts: [pronoun] }] });

        basicTenses.forEach((tense) => {
          const person = speaker[index];
          cells.push(getBodyCell(time[tense][person]));
        });

        table.cells.push(cells);
      });

      break;
    }

    case "perfect_tenses": {
      const time = verb[mood];

      table.header = "Konjunktiv, sammensatte tider";
      table.cells = [
        [
          { type: "sub-header", elements: [{}] },
          { elements: [{ texts: ["Perfektum"], comment: "(førnutid)" }], type: "sub-header" },
          { elements: [{ texts: ["Pluskvamperfektum"], comment: "(førdatid)" }], type: "sub-header" },
          { elements: [{ texts: ["Futurum perfektum"], comment: "(førfremtid)" }], type: "sub-header" }
        ]
      ];

      pronounList.forEach((pronoun, index) => {
        const cells: DictsysInflectionSchemeCell[] = [];
        cells.push({ type: "sub-header", elements: [{ texts: [pronoun] }] });

        perfectTenses.forEach((tense) => {
          const person = speaker[index];
          cells.push(getBodyCell(time[tense][person]));
        });

        table.cells.push(cells);
      });

      break;
    }

    case "imperative": {
      const time = verb[mood];

      table.header = "Imperativ";
      table.cells = [
        [
          { type: "sub-header", elements: [{}] },
          { elements: [{ texts: ["Bekræftende"] }], type: "sub-header" },
          { elements: [{ texts: ["Benægtende"] }], type: "sub-header" }
        ]
      ];

      shortPronounList.forEach((pronoun, index) => {
        const cells: DictsysInflectionSchemeCell[] = [];
        cells.push({ type: "sub-header", elements: [{ texts: [pronoun] }] });

        imperativeTenses.forEach((tense) => {
          const person = imperativeSpeaker[index];

          if (person === "first_person_singular") {
            cells.push({ type: "body", elements: [{ texts: ["-"] }] });
            return;
          }

          cells.push(getBodyCell(time[tense][person], "always"));
        });
        table.cells.push(cells);
      });

      break;
    }

    case "near_future": {
      const time = verb.infinitive.present[0];

      table.extraTableHeading = "Nær fremtid";
      table.header = "Nær fremtid med ir + a + infinitiv";
      table.separatorIndex = 2;

      const prefixes: Record<Person, string> = {
        first_person_singular: "voy a",
        second_person_singular: "vas a",
        third_person_he_singular: "va a",
        third_person_she_singular: "va a",
        third_person_it_singular: "va a",
        first_person_plural: "vamos a",
        second_person_plural: "vais a",
        third_person_plural: "van a"
      };

      pronounList.forEach((pronoun, index) => {
        const person = speaker[index];
        const prefix = prefixes[person];

        table.cells.push([
          { type: "sub-header", elements: [{ texts: [pronoun] }] },
          { type: "body", elements: [{ texts: [`${prefix} ${time.inflexion}`] }] }
        ]);
      });

      break;
    }
  }

  return table;
}

/** @see DictsysInflectionSchemeHelper.getBodyCell() */
function getBodyCell(
  input: DictsysInflectionSchemeContent[] | null,
  prefixMode: "first" | "always" = "first"
): DictsysInflectionSchemeCell {
  const cell = DictsysInflectionSchemeHelper.getBodyCell(input, prefixMode);

  const texts = cell.elements
    .map((element) => element.texts)
    .filter((text) => text !== undefined)
    .flat()
    .map((text, index) => {
      // Spanish uses special "el." for alternatives, instead of slashes
      if (index === 0) {
        return text;
      }

      return `𝘦𝘭. ${text}`;
    })
    // getBodyCell() adds spacing after the prefix. It shouldn't for Spanish exclamations.
    .map((text) => text!.replace("¡ ", "¡"));

  cell.elements = [{ texts }];

  return cell;
}
